<template>
  <section class="admin-content" id="contact-search">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex flex-wrap align-items-center text-white" style="padding: 32px 0; gap: 18px;">
            <div class="header-icon-wrap tpa-initial">{{ initial }}</div>
            <div class="flex-grow-1" style="min-width: 220px;">
              <small class="text-uppercase font-weight-bold" style="opacity: .75; letter-spacing: .08em;">HMO / TPA</small>
              <h3 class="page-title text-white mb-2">{{ tpa.organization_name || (notFound ? 'Not found' : '…') }}</h3>
              <div class="d-flex flex-wrap" style="gap: 8px;" v-if="tpa.id">
                <span class="header-status" :class="isActive ? 'header-status--active' : 'header-status--inactive'">
                  <i class="fe mr-1" :class="isActive ? 'fe-check-circle' : 'fe-pause-circle'"></i>
                  {{ isActive ? 'Active' : 'Inactive' }}
                </span>
                <span v-if="tpa.tpa_id" class="header-id-badge"><i class="fe fe-hash mr-1"></i> {{ tpa.tpa_id }}</span>
                <span v-if="tpa.category" class="header-id-badge"><i class="fe fe-tag mr-1"></i> {{ tpa.category }}</span>
                <span v-if="tpa.email" class="header-id-badge"><i class="fe fe-mail mr-1"></i> {{ tpa.email }}</span>
                <span v-if="tpa.phone_number" class="header-id-badge"><i class="fe fe-phone mr-1"></i> {{ tpa.phone_number }}</span>
              </div>
            </div>
            <div class="d-flex flex-wrap" style="gap: 8px;">
              <router-link to="/my-tpas" class="btn btn-outline-light btn-sm font-weight-bold">
                <i class="fe fe-arrow-left mr-1"></i> All HMOs
              </router-link>
              <template v-if="tpa.id && canManage">
                <router-link :to="'/edit-tpa/' + tpa.id" class="btn btn-light btn-sm font-weight-bold">
                  <i class="fe fe-edit-2 mr-1"></i> Edit
                </router-link>
              </template>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">

        <div v-if="notFound" class="card action-card shadow-sm">
          <div class="card-body text-center text-muted py-5">
            <i class="fe fe-alert-circle d-block mb-2" style="font-size: 2rem;"></i>
            This HMO / TPA could not be found.
            <div class="mt-3"><router-link to="/my-tpas" class="btn btn-sm btn-outline-primary">Back to the list</router-link></div>
          </div>
        </div>

        <template v-else>
          <!-- ── KPI tiles ── -->
          <div class="row stat-tiles">
            <div class="col-lg-4 col-sm-6 mb-4" v-for="kpi in kpis" :key="kpi.label">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-4 d-flex align-items-center" style="gap: 14px;">
                  <div class="avatar avatar-md flex-shrink-0">
                    <div class="avatar-title rounded-circle" :class="kpi.tone"><i :class="kpi.icon"></i></div>
                  </div>
                  <div style="min-width: 0;">
                    <p class="mb-0 text-muted" style="font-size: .72rem; text-transform: uppercase; letter-spacing: .04em;">{{ kpi.label }}</p>
                    <h4 class="mb-0 font-weight-bold text-dark text-truncate">{{ kpi.value }}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <!-- ── Details ── -->
            <div class="col-lg-4 mb-4">
              <div class="card shadow-sm form-card h-100">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-briefcase" style="font-size: 12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Organisation details</h6>
                </div>
                <ul class="p-0 m-0">
                  <li class="info-row" v-for="row in detailRows" :key="row.label">
                    <i class="info-icon" :class="row.icon"></i>
                    <div style="min-width: 0;">
                      <p class="info-label">{{ row.label }}</p>
                      <p class="info-value text-break">{{ row.value }}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <!-- ── Enrollees ── -->
            <div class="col-lg-8 mb-4">
              <div class="card table-card shadow-sm h-100">
                <div class="d-flex align-items-center justify-content-between px-4 pt-4 pb-3">
                  <div>
                    <h6 class="mb-0 font-weight-bold text-dark"><i class="fe fe-users text-primary mr-2"></i> Enrollees</h6>
                    <small class="text-muted">{{ formatNumber(meta.total) }} enrolled with this HMO</small>
                  </div>
                </div>
                <div class="table-responsive">
                  <table class="table mb-0">
                    <thead class="table-head">
                      <tr>
                        <th class="table-th px-4">Name</th>
                        <th class="table-th">Phone</th>
                        <th class="table-th">Programme</th>
                        <th class="table-th">ID number</th>
                        <th class="table-th"><span class="sr-only">Action</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="loadingClients">
                        <td colspan="5" class="text-center text-muted py-5">Loading enrollees…</td>
                      </tr>
                      <template v-else>
                        <tr v-for="enrollee in clients" :key="enrollee.id" class="table-row">
                          <td class="px-4 py-3">
                            <div class="d-flex align-items-center" style="gap: 10px;">
                              <div class="employee-avatar">{{ (enrollee.full_name || enrollee.firstname || '?')[0].toUpperCase() }}</div>
                              <router-link :to="'/client/' + enrollee.id" class="font-weight-bold" style="font-size: .88rem;">
                                {{ enrollee.full_name || [enrollee.firstname, enrollee.lastname].filter(Boolean).join(' ') || '—' }}
                              </router-link>
                            </div>
                          </td>
                          <td class="py-3" style="font-size: .88rem;">{{ enrollee.phone_number || '—' }}</td>
                          <td class="py-3"><span v-if="enrollee.sector" class="rel-chip">{{ enrollee.sector }}</span><span v-else class="text-muted">—</span></td>
                          <td class="py-3" style="font-size: .85rem;">{{ enrollee.id_card_number || '—' }}</td>
                          <td class="py-3 text-right pr-4">
                            <router-link :to="'/client/' + enrollee.id" class="btn btn-sm btn-outline-primary" :aria-label="'View ' + (enrollee.full_name || 'enrollee')">
                              <i class="fe fe-eye"></i>
                            </router-link>
                          </td>
                        </tr>
                        <tr v-if="!clients.length">
                          <td colspan="5" class="text-center text-muted py-5">
                            <i class="fe fe-users d-block mb-2" style="font-size: 2rem;"></i>
                            No enrollees with this HMO yet
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
                <div v-if="meta.last_page > 1" class="d-flex align-items-center justify-content-between px-4 py-3 border-top">
                  <small class="text-muted">Page {{ meta.current_page }} of {{ meta.last_page }}</small>
                  <div class="d-flex" style="gap: 8px;">
                    <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="meta.current_page <= 1 || loadingClients"
                      @click="getTPAClients(meta.current_page - 1)"><i class="fe fe-chevron-left"></i> Previous</button>
                    <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="meta.current_page >= meta.last_page || loadingClients"
                      @click="getTPAClients(meta.current_page + 1)">Next <i class="fe fe-chevron-right"></i></button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="true"></loading>
      </div>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

export default {
  components: {
    Navbar,
    Loading,
  },
  data() {
    return {
      user: null,
      tpa: {},
      clients: [],
      meta: { total: 0, current_page: 1, last_page: 1 },
      notFound: false,
      isLoading: false,
      loadingClients: false,
    };
  },
  computed: {
    initial() {
      return (this.tpa.organization_name || "?").trim().charAt(0).toUpperCase();
    },
    isActive() {
      return this.tpa.org_status === undefined || this.tpa.org_status === null || Number(this.tpa.org_status) === 1;
    },
    // Agency admins (the agency account, or staff with user_role 1) edit HMOs and their personnel.
    canManage() {
      return !!this.user && (this.user.type === "shis" || Number(this.user.user_role) === 1);
    },
    kpis() {
      return [
        { label: "Enrollees", value: this.formatNumber(this.meta.total), icon: "fe fe-users", tone: "bg-soft-primary text-primary" },
        { label: "Status", value: this.isActive ? "Active" : "Inactive", icon: this.isActive ? "fe fe-check-circle" : "fe fe-pause-circle", tone: this.isActive ? "bg-soft-success text-success" : "bg-soft-warning text-warning" },
        { label: "Registered", value: this.formatDate(this.tpa.created_at), icon: "fe fe-calendar", tone: "bg-soft-info text-info" },
      ];
    },
    detailRows() {
      const t = this.tpa;
      return [
        { label: "Organisation", value: t.organization_name || "—", icon: "fe fe-briefcase" },
        { label: "Email", value: t.email || "—", icon: "fe fe-mail" },
        { label: "Phone number", value: t.phone_number || "—", icon: "fe fe-phone" },
        { label: "HMO code", value: t.tpa_id || "—", icon: "fe fe-hash" },
        { label: "Office address", value: t.address || "—", icon: "fe fe-map-pin" },
        { label: "Category", value: t.category || "—", icon: "fe fe-tag" },
        { label: "Last updated", value: this.formatDate(t.updated_at), icon: "fe fe-clock" },
      ];
    },
  },
  watch: {
    "$route.params.id"() {
      this.load();
    },
  },
  created() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.load();
  },
  methods: {
    load() {
      this.getTPA();
      this.getTPAClients(1);
    },
    getTPA() {
      this.isLoading = true;
      this.notFound = false;
      this.axios
        .get(`/api/v1/auth/orgenrollment/${this.$route.params.id}`)
        .then((response) => {
          this.tpa = response.data || {};
          this.notFound = !response.data;
        })
        .catch((error) => {
          console.error(error);
          this.notFound = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getTPAClients(page) {
      this.loadingClients = true;
      this.axios
        .get(`/api/v1/auth/org_client/${this.$route.params.id}`, { params: { page } })
        .then((response) => {
          this.clients = (response.data && response.data.data) || [];
          this.meta = Object.assign({ total: 0, current_page: 1, last_page: 1 }, response.data && response.data.meta);
        })
        .catch((error) => {
          console.error(error);
          this.clients = [];
        })
        .finally(() => {
          this.loadingClients = false;
        });
    },
    formatNumber(n) {
      return Number(n || 0).toLocaleString();
    },
    formatDate(value) {
      if (!value) return "—";
      const d = new Date(String(value).replace(" ", "T"));
      return isNaN(d) ? value : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    },
  },
};
</script>

<style scoped>
.tpa-initial {
  width: 64px;
  height: 64px;
  font-size: 1.6rem;
  font-weight: 800;
}
</style>
